<script>
import {mapState} from "pinia";
import {useUsersStore} from "@nextories/vue-tasks/src/store/usersStore.ts";
import {useProjectsStore} from "@nextories/vue-tasks/src/store/projectsStore.ts";
import veeValidate from "@nextories/vue-tasks/src/utils/modules/veeValidate.ts";

export default {
  name: "MeetingFields",
  nameClassStyle: "input-container",
  emits: ['update:operators', 'update:duration', 'update:scheduled', 'update:language', 'update:meetingType', 'update:description', 'update:dateTypeSelected', 'update:meetingDate'],
  props: {
    duration: Number,
    language: String,
    description: String,
    meetingDate: [Object, String],
    operators: {
      type: [Array, String],
      default: () => [],
    },
    meetingType: String,
    dateTypeSelected: String,
    scheduled: Object,
    fields: Object,
  },
  data() {
    return {
      currentFields: {},
    };
  },
  created() {
    this.currentFields = this.$helpers.format.deepMergeFlatten({
      meetingType: {
        id: 'meeting_type',
        hide: false,
        separator: undefined,
        value: this.meetingType,
        required: true,
      },
      language: {
        id: 'meeting_language',
        hide: false,
        options: this.$tm(`meeting.select.languages`),
        placeholder: this.$t('meeting.select.defaultLanguage'),
        value: this.language,
        separator: undefined,
      },
      duration: {
        id: 'meeting_duration',
        hide: false,
        clearable: true,
        options: this.$tm(`meeting.select.durations`),
        placeholder: this.$t('meeting.select.defaultDuration'),
        value: this.duration,
        separator: undefined,
      },
      operators: {
        id: 'meeting_operators',
        hide: false,
        value: [],
        clearable: true,
        collapse_tags: true,
        is_value_object: true,
        placeholder: this.$t('meeting.select.defaultOperator'),
        multiple: true,
        options: this.listOperators,
        option_label: 'title',
        option_value: 'address_state',
        popper_append_to_body: true,
        separator: '',
      },
      description: {
        id: 'meeting_description',
        hide: false,
        placeholder: this.$t('meeting.description'),
        value: this.description,
        type: 'textarea',
      },
      vadContent: {
        id: 'meeting_vad_content',
        hide: true,
        refValues: {
          dgt: this.project,
        },
        type: 'html-content'
      },
      dateTypeSelected: {
        id: 'meeting_date_type',
        hide: true,
        value: this.dateTypeSelected,
        required: true,
        errors: {
          required: this.$t('meeting.fields.dateType.required'),
        },
        options: this.$tm(`meeting.select.dateTypeSelected`),
        type: 'selector',
      },
      meetingDate: {
        id: 'meeting_date',
        hide: true,
        value: this.meetingDate,
        display_name: this.$tm(`meeting.label.meetingDate`),
        default_value: this.$filters.date.objectDate(this.meetingDate),
        default_time: this.$filters.date.objectDate(this.meetingDate),
        type: 'date-time',
        required: true,
        field_date: {
          required: true,
        },
        field_time: {
          required: true,
          clearable: true,
        },
      },
      scheduled: {
        id: 'meeting_Scheduled',
        hide: true,
        value: this.scheduled,
        fields: this.fields,
        type: 'scheduled'
      },
    }, this.fields);
    for (const [id, field] of Object.entries(this.currentFields)) {
      if (field.errors) {
        veeValidate.setMessageErrors(id, field.errors);
      }
    }
    if (this.currentFields.meetingDate && this.meetingDate
      && !this.$helpers.date.isValidDateFormat(this.meetingDate, 'YYYY-MM-DDTHH:mm:ssZ')) {
      this.currentFields.meetingDate.value = this.$filters.date.formatDateCustom(this.meetingDate, 'YYYY-MM-DDTHH:mm:ssZ');
    }
    if (this.meetingType && this.currentFields.meetingType.options && this.currentFields.meetingType.options.length) {
      let findMeetingType = this.currentFields.meetingType.options.find((item) => item.value === this.meetingType);
      findMeetingType = findMeetingType ? findMeetingType.value : undefined;
      if (this.currentFields.meetingType.value !== findMeetingType) {
        this.currentFields.meetingType.value = findMeetingType;
        this.$emit(`update:meetingType`, this.currentFields.meetingType.value);
      }
    }
    this.currentFields.operators.value = Array.isArray(this.operators) ? this.operators.filter((operator) => this.listOperators.some((item) => item.address_state === operator)) || [] : this.operators;
    this.$emit(`update:operators`, this.currentFields.operators.value);
  },
  computed: {
    ...mapState(useUsersStore, ["users"]),
    ...mapState(useProjectsStore, ["project", "steps"]),
    listOperators() {
      const users = [];
      for (const user of (this.users && Object.values(this.users)) || []) {
        if (user && user.address_state && (this.meetingType === 'VISIT_TYPE_CLASSIC' ? !!user.vad : true)) {
          users.push({
            ...user,
            title: this.$t('filters.userTitle', {
              firstName: user.first_name || '',
              lastName: user.last_name || '',
            }),
          });
        }
      }
      return users;
    },
  },
  methods: {
    updateModel(id, value) {
      this.$emit(`update:${id}`, value);
    },
    updateDuration(data) {
      this.currentFields.duration.value = data;
    },
    rulesInput(field) {
      return this.$helpers.fieldUtils.rulesInput(field, field.type, this.steps);
    },
    getTypeField(type) {
      if (['scheduled'].some((t) => t === type)) {
        return type;
      }
      if (['date', 'date-time'].some((t) => t === type)) {
        return `nxt-tasks-input-${type}`;
      }
      if (['html-content'].some((t) => t === type)) {
        return `nxt-core-${type}`;
      }
      return `nxt-core-input-${type || 'select'}`;
    },
  },
};
</script>

<template>
  <vee-form :class="['fields', dateTypeSelected ? `--${dateTypeSelected}` : '']">
    <template v-for="[id, field] of Object.entries(currentFields)">
      <div v-if="field && !field.hide"
           :class="[$setClasses, `field--${field.type}`]">
        <vee-field v-slot="{errors, meta}"
                   :label="field.short_name || field.display_name || ' '"
                   :modelValue="field.value"
                   :name="id"
                   :rules="rulesInput(field)"
                   ref="provider-primary"
                   validateOnChange
                   validateOnModelUpdate
                   validateOnMount>
          <nxt-core-field
            :class="field.required ? field.display_name ? '--required' : '--is-required' : ''"
            :label="field.display_name"
            :placeholder="field.placeholder"
            :popover="field.display_name && field.popover">
            <div>
              <component :is="getTypeField(field.type)"
                         v-model="field.value"
                         :class="['el-input', `input-type-${field.type || 'select'}`,
                    !!(!field.display_name && field.popover) ? 'is-popover' : '',
                    meta.valid && 'is-valid', !meta.valid && 'is-invalid']"
                         :clearable="!!field.clearable"
                         :collapse-tags="!!field.collapse_tags"
                         :content="field.content"
                         :disabled="field.disabled"
                         :field="field"
                         :field-date="field.field_date || undefined"
                         :field-time="field.field_time || undefined"
                         :fields="field.fields"
                         :is-value-object="!!field.is_value_object"
                         :multiple="!!field.multiple"
                         :option-label="field.option_label || 'display_name'"
                         :option-value="field.option_value || 'value'"
                         :options="field.options"
                         :placeholder="field.placeholder"
                         :popper-append-to-body="field.popper_append_to_body"
                         :ref-values="field.refValues"
                         :required="field.required"
                         :separator="field.separator"
                         @update:duration="updateDuration"
                         @update:modelValue="updateModel(id, field.value)">
                <template v-if="id === 'operators'"
                          v-slot:item="scope">
                  <nxt-tasks-avatar :user="scope.item"
                                    size="small">
                    <template v-slot:after>
                      {{ scope.item.title }}
                    </template>
                  </nxt-tasks-avatar>
                </template>
              </component>
            </div>
          </nxt-core-field>
          <ul :class="[$vClass('list')]">
            <li v-for="(error, index) in errors"
                :key="`error-${index}`">
              <nxt-core-text-content :class="[$vPrefix('text--error')]"
                                     tag="span">
                {{ error }}
              </nxt-core-text-content>
            </li>
          </ul>
        </vee-field>
      </div>
    </template>
    <slot></slot>
  </vee-form>
</template>

<style lang="scss" scoped>
@use "sass:math";
@use "@nextories/vue-core-components/src/assets/scss/components/organisms/NxtCoreInputContainer" with ($prefixName: $prefixName);

.is-invalid {
  :deep(.el-select__wrapper), :deep(.el-input__wrapper) {
    box-shadow: 0 0 0 1px var(--nxt-color-danger) inset;
  }
}

.input-container {
  padding-top: var(--nxt-space-default);

  .field.--is-required:after {
    content: "*";
    display: block;
    position: absolute;
    right: -15px;
    font-size: 30px;
    color: var(--nxt-color-primary);
    top: 10px;
  }

  &__list {
    padding: 0;
    margin: 0;
  }
}

.fields:not(.--scheduled) {
  :deep(.meeting-display) {
    margin-top: var(--nxt-space-default);
  }
}

@include breakpoint('desktop') {
  .fields.--scheduled {
    $--nb-columns: 6;

    display: grid;
    grid-template-columns: repeat(calc($--nb-columns - 2), 1fr) minmax(1fr, max-content);
    align-content: start;
    column-gap: var(--nxt-space-default);

    .input-container {
      grid-column: 1 / #{$--nb-columns};
    }

    .field--scheduled {
      grid-column: 1 / 2;
    }

    :deep(.meeting-display) {
      grid-column: 2 / #{$--nb-columns};
      margin: var(--nxt-space-default) 0 0;
      align-self: start;
    }
  }
}
</style>
